import isAfter from 'date-fns/isAfter';
import isSameDay from 'date-fns/isSameDay';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import { useEffect, useMemo, useRef } from 'react';

import { isEmpty } from '../../common/misc';
import type { GuideWithAvailability } from '../../guidance/types/Guide';
import {
    AVAILABILITY_QUERY_DAYS_LIMIT,
    useGetGuidesWithFirstSessionAvailability,
} from '../../guidanceSessions/hooks/useGetGuidesWithFirstSessionAvailability';
import {
    getAvailabilityQueryTimeRanges,
    getEarliestAvailabilityDay,
    getLatestAvailabilityDay,
    logAvailabilityStats,
    mergeGuidesWithAvailability,
} from '../utils/bookAvailableGuides';

const AVAILABILITY_WINDOW_COUNT = 3;
const DAYS_BUFFER_TO_FETCH_NEXT = 3;

const countAvailableSessions = (
    guidesWithAvailability: GuideWithAvailability[] | null,
): number =>
    (guidesWithAvailability ?? []).reduce(
        (total, guide) => total + (guide.sessionAvailableTimes?.length ?? 0),
        0,
    );

export const useGuidesWithFirstSessionAvailabilityData = ({
    initDate,
    selectedDay,
    membershipId,
    membershipGuideId,
}: {
    initDate: Date;
    selectedDay: Date;
    membershipId: string;
    membershipGuideId?: string;
}) => {
    const { dateRange1, dateRange2, dateRange3 } = useMemo(
        () =>
            getAvailabilityQueryTimeRanges(
                initDate,
                AVAILABILITY_QUERY_DAYS_LIMIT,
            ),
        [initDate],
    );

    const availabilityQueryChunk1 = useGetGuidesWithFirstSessionAvailability({
        membershipId,
        start: dateRange1.start,
        end: dateRange1.end,
        enabled: !isEmpty(membershipId),
    });
    const chunk1HasSparseAvailability = useMemo(
        () =>
            availabilityQueryChunk1.guidesWithAvailability != null &&
            countAvailableSessions(
                availabilityQueryChunk1.guidesWithAvailability,
            ) < 10,
        [availabilityQueryChunk1.guidesWithAvailability],
    );
    const readyToFetchChunk2 = useMemo(
        () =>
            isAfter(
                selectedDay,
                subDays(dateRange1.end, DAYS_BUFFER_TO_FETCH_NEXT),
            ) || chunk1HasSparseAvailability,
        [selectedDay, dateRange1.end, chunk1HasSparseAvailability],
    );
    const availabilityQueryChunk2 = useGetGuidesWithFirstSessionAvailability({
        membershipId,
        start: dateRange2.start,
        end: dateRange2.end,
        enabled: !isEmpty(membershipId) && readyToFetchChunk2,
    });
    const chunk2HasSparseAvailability = useMemo(
        () =>
            availabilityQueryChunk2.guidesWithAvailability != null &&
            countAvailableSessions(
                availabilityQueryChunk2.guidesWithAvailability,
            ) < 5,
        [availabilityQueryChunk2.guidesWithAvailability],
    );
    const readyToFetchChunk3 = useMemo(
        () =>
            isAfter(
                selectedDay,
                subDays(dateRange2.end, DAYS_BUFFER_TO_FETCH_NEXT),
            ) || chunk2HasSparseAvailability,
        [selectedDay, dateRange2.end, chunk2HasSparseAvailability],
    );
    const availabilityQueryChunk3 = useGetGuidesWithFirstSessionAvailability({
        membershipId,
        start: dateRange3.start,
        end: dateRange3.end,
        enabled: !isEmpty(membershipId) && readyToFetchChunk3,
    });

    const allGuidesWithAvailability = useMemo(
        () =>
            mergeGuidesWithAvailability(
                availabilityQueryChunk1.guidesWithAvailability ?? [],
                availabilityQueryChunk2.guidesWithAvailability ?? [],
                availabilityQueryChunk3.guidesWithAvailability ?? [],
            ),
        [
            availabilityQueryChunk1.guidesWithAvailability,
            availabilityQueryChunk2.guidesWithAvailability,
            availabilityQueryChunk3.guidesWithAvailability,
        ],
    );
    const allAvailabilityRangesLoaded = useMemo(
        () =>
            availabilityQueryChunk1.guidesWithAvailability != null &&
            availabilityQueryChunk2.guidesWithAvailability != null &&
            availabilityQueryChunk3.guidesWithAvailability != null,
        [
            availabilityQueryChunk1.guidesWithAvailability,
            availabilityQueryChunk2.guidesWithAvailability,
            availabilityQueryChunk3.guidesWithAvailability,
        ],
    );
    const hasNoAvailabilityAcrossRanges = useMemo(
        () =>
            allAvailabilityRangesLoaded &&
            !allGuidesWithAvailability.some(
                (guide) => !isEmpty(guide.sessionAvailableTimes),
            ),
        [allAvailabilityRangesLoaded, allGuidesWithAvailability],
    );

    const minNavigableDay = useMemo(() => startOfDay(initDate), [initDate]);
    const maxNavigableDay = useMemo(
        () =>
            availabilityQueryChunk2.isPending
                ? dateRange1.end
                : availabilityQueryChunk3.isPending
                  ? dateRange2.end
                  : dateRange3.end,
        [
            availabilityQueryChunk2.isPending,
            availabilityQueryChunk3.isPending,
            dateRange1.end,
            dateRange2.end,
            dateRange3.end,
        ],
    );
    const earliestDayWithAvailability = useMemo(
        () =>
            getEarliestAvailabilityDay(
                availabilityQueryChunk1.guidesWithAvailability ?? [],
            ) ?? dateRange1.start,
        [availabilityQueryChunk1.guidesWithAvailability, dateRange1.start],
    );
    const latestDayWithAvailability = useMemo(
        () =>
            getLatestAvailabilityDay(allGuidesWithAvailability) ??
            dateRange3.end,
        [allGuidesWithAvailability, dateRange3.end],
    );
    const isOnEarliestDayWithAvailability = useMemo(
        () => isSameDay(selectedDay, earliestDayWithAvailability),
        [selectedDay, earliestDayWithAvailability],
    );
    const isOnLatestDayWithAvailability = useMemo(
        () => isSameDay(selectedDay, latestDayWithAvailability),
        [selectedDay, latestDayWithAvailability],
    );

    const availabilityChunk1LoggedRef = useRef(false);
    const availabilityChunk2LoggedRef = useRef(false);
    const availabilityChunk3LoggedRef = useRef(false);

    useEffect(() => {
        if (!availabilityChunk1LoggedRef.current) {
            availabilityChunk1LoggedRef.current = logAvailabilityStats({
                chunkIndex: 1,
                query: availabilityQueryChunk1,
                timeRange: dateRange1,
                membershipGuideId,
            });
        }
    }, [dateRange1, availabilityQueryChunk1, membershipGuideId]);
    useEffect(() => {
        if (!availabilityChunk2LoggedRef.current) {
            availabilityChunk2LoggedRef.current = logAvailabilityStats({
                chunkIndex: 2,
                query: availabilityQueryChunk2,
                timeRange: dateRange2,
                membershipGuideId,
                enabled: readyToFetchChunk2,
            });
        }
    }, [
        dateRange2,
        readyToFetchChunk2,
        availabilityQueryChunk2,
        membershipGuideId,
    ]);
    useEffect(() => {
        if (!availabilityChunk3LoggedRef.current) {
            availabilityChunk3LoggedRef.current = logAvailabilityStats({
                chunkIndex: 3,
                query: availabilityQueryChunk3,
                timeRange: dateRange3,
                membershipGuideId,
                enabled: readyToFetchChunk3,
            });
        }
    }, [
        dateRange3,
        readyToFetchChunk3,
        availabilityQueryChunk3,
        membershipGuideId,
    ]);

    return {
        dateRange1,
        dateRange2,
        dateRange3,
        readyToFetchChunk2,
        readyToFetchChunk3,
        availabilityQueryChunk1,
        availabilityQueryChunk2,
        availabilityQueryChunk3,
        allGuidesWithAvailability,
        earliestDayWithAvailability,
        latestDayWithAvailability,
        isOnEarliestDayWithAvailability,
        isOnLatestDayWithAvailability,
        hasNoAvailabilityAcrossRanges,
        minNavigableDay,
        maxNavigableDay,
        totalDaysInRanges:
            AVAILABILITY_QUERY_DAYS_LIMIT * AVAILABILITY_WINDOW_COUNT - 1,
    };
};
